/**
 * /sources/collection/$collectionId — read-only detail view for a single
 * indexed group or chat (a "collection").
 *
 * Shows:
 *  - Header: group icon + title + ingestion status pill + visibility badge.
 *  - Stats row: last activity and message count.
 *  - Recent messages section (sender, snippet, relative timestamp).
 *
 * Loading → Skeleton; fetch error → inline destructive text.
 * "Load more" via cursor-based pagination (nextCursor from the messages endpoint).
 *
 * Routing note: TanStack Router prefers the static segment "collection" over
 * the dynamic $connector param, so this path (/sources/collection/$collectionId)
 * does NOT get shadowed by /sources/$connector/$accountId. This mirrors the
 * same pattern used by /sources/signal/import (static "signal") vs
 * /sources/$collectionId/import (dynamic).
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useParams } from '@tanstack/react-router';
import { ChevronLeft, MessageSquare, Users } from 'lucide-react';
import { IconTile } from '@/components/patterns/icon-tile';
import { StatCard } from '@/components/patterns/stat-card';
import { StatusPill } from '@/components/patterns/status-pill';
import { formatRelative, MessageLog } from '@/components/sources/message-log';
import { buttonVariants } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { collectionApi } from '@/lib/api';
import { qk } from '@/lib/query-client';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CollectionDetailPage() {
  const { collectionId = '' } = useParams({ strict: false }) as {
    collectionId?: string;
  };

  const collectionQuery = useQuery({
    queryKey: qk.collection(collectionId),
    queryFn: () => collectionApi.get(collectionId),
    enabled: Boolean(collectionId),
  });

  const messagesQuery = useInfiniteQuery({
    queryKey: qk.collectionMessages(collectionId),
    queryFn: ({ pageParam }) =>
      collectionApi.listMessages(collectionId, {
        limit: 50,
        before: pageParam,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: Boolean(collectionId),
  });

  const collection = collectionQuery.data;
  const allMessages = messagesQuery.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div>
      {/* Back link */}
      <Link
        to="/sources"
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'sm' }),
          '-ml-2 mb-4 inline-flex items-center gap-1 text-[13px] text-muted-foreground',
        )}
      >
        <ChevronLeft className="h-4 w-4" />
        Sources
      </Link>

      {/* Header */}
      {collectionQuery.isLoading ? (
        <div className="flex items-center gap-4">
          <Skeleton className="h-[44px] w-[44px] rounded-[11px]" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
      ) : collectionQuery.isError ? (
        <p className="text-sm text-destructive">
          Failed to load collection —{' '}
          {(collectionQuery.error as { message?: string })?.message ?? 'unknown error'}
        </p>
      ) : collection ? (
        <div className="flex items-center gap-4">
          <IconTile tone="neutral">
            {collection.type === 'dm' ? (
              <MessageSquare className="h-5 w-5" />
            ) : (
              <Users className="h-5 w-5" />
            )}
          </IconTile>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-bold tracking-[-0.02em]">
              {collection.title}
            </h1>
          </div>
          {/* Visibility badge */}
          <span className="shrink-0 rounded-full border border-border-subtle bg-muted px-2.5 py-1 text-xs font-semibold text-faint">
            {collection.visibility === 'workspace' ? 'Shared' : 'Private'}
          </span>
          {/* Ingestion status */}
          <StatusPill tone={collection.ingestionEnabled ? 'active' : 'paused'}>
            {collection.ingestionEnabled ? 'Indexing' : 'Paused'}
          </StatusPill>
        </div>
      ) : null}

      {/* Stats row */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {collectionQuery.isLoading ? (
          <>
            <Skeleton className="h-[104px] rounded-xl" />
            <Skeleton className="h-[104px] rounded-xl" />
          </>
        ) : collection ? (
          <>
            <StatCard
              label="Last activity"
              value={formatRelative(collection.lastActivityAt)}
              sub="Most recent message ingested in this chat"
            />
            <StatCard
              label="Messages indexed"
              value={collection.messageCount.toLocaleString()}
              sub="Total messages indexed in this collection"
            />
          </>
        ) : null}
      </div>

      {/* Recent messages */}
      <div className="mt-6">
        <MessageLog
          isLoading={messagesQuery.isLoading}
          isError={messagesQuery.isError}
          error={messagesQuery.error}
          messages={allMessages}
          hasNextPage={messagesQuery.hasNextPage}
          isFetchingNextPage={messagesQuery.isFetchingNextPage}
          onLoadMore={() => void messagesQuery.fetchNextPage()}
        />
      </div>
    </div>
  );
}
